import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as Papa from 'papaparse';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { EnvironmentAdapter } from '../environment/environment.adapter';
import { LanguageChangeEvent } from './model/language-change.model';
import { LanguageDirection } from './model/language-direction.model';
import { Language } from './model/language.model';
import { Translations } from './model/translations.model';

@Injectable({
  providedIn: 'root'
})
export class TranslationAdapter {
  constructor(
    private httpClient: HttpClient,
    private environmentAdapter: EnvironmentAdapter,
    private translateService: TranslateService,
  ) {}

  isLanguageSupported(language: string): Observable<boolean> {
    const isSupportedObs = this.environmentAdapter.getEnvironment()
      .pipe(
        switchMap((environment) => {
          const translationsFilepath = environment.browserAppConfig.translationsFilepath;
          return this.httpClient.get(translationsFilepath, { responseType: 'text' });
        }),
        map((csvData) => {
          const data = Papa.parse<string[]>(csvData).data;
          const header = data[0];
          const isLanguageSupported = header.includes(language);

          return isLanguageSupported;
        }),
        catchError((_) => of(false))
      );

    return isSupportedObs;
  }

  setLanguage(language: string): Observable<void> {
    const obs = this.translateService.use(language).pipe(
      map((_) => { /** No-op */ })
    );

    return obs;
  }

  setDefaultLanguage(language: string): Observable<void> {
    this.translateService.setDefaultLang(language);
    return of(undefined);
  }

  getCurrentLangauge(): Observable<string> {
    const langauge = this.translateService.currentLang;
    const languageObs = of(langauge);

    return languageObs;
  }

  getCurrentCulturalLanguage(): Observable<string> {
    const language = this.translateService.getBrowserCultureLang();
    return of(language);
  }

  getBrowserCurrentLanguage(): Observable<string> {
    const language = this.translateService.getBrowserLang();
    return of(language);
  }

  getDefaultLanguage(): Observable<string> {
    const language = this.translateService.getDefaultLang();
    return of(language);
  }

  getLanguageChangeStream(): Observable<LanguageChangeEvent> {
    const stream = this.translateService.onLangChange.pipe(
      map((langChangeEvent, _) => {
        const direction = langChangeEvent.lang === Language.ARABIC
          ? LanguageDirection.RIGHT_TO_LEFT
          : LanguageDirection.LEFT_TO_RIGHT;
        const language = Language.fromLocale(langChangeEvent.lang);

        const languageChange: LanguageChangeEvent = {
          language: language,
          translations: langChangeEvent.translations,
          direction: direction
        };

        return languageChange;
      }),
    );

    return stream;
  }

  getTranslationsForLanguage(language: string): Observable<Translations> {
    const translations = this.translateService.translations[language];
    const translationsObs = of(translations);
    return translationsObs;
  }
}
